import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const refCode = params.get("ref");
    const inviteToken = params.get("invite");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [inviteInfo, setInviteInfo] = useState(null);

    // Look up invite details so we can pre-fill email and show the invitation context
    useEffect(() => {
        if (!inviteToken) return;
        import("@/lib/api").then((m) =>
            m.default.get(`/team/invite/${inviteToken}/lookup`)
                .then((r) => { setInviteInfo(r.data); setEmail(r.data.invited_email || ""); })
                .catch(() => { toast.error("This invite link is invalid or already used"); })
        );
    }, [inviteToken]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(name, email, password, refCode, inviteToken);
            toast.success(inviteToken ? "Account created — you're on the team!" : refCode ? "Account created — your referrer got a free month!" : "Account created");
            nav("/app");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            <div className="hidden lg:flex bg-card grain-bg border-r border-border relative flex-col justify-between p-12">
                <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2">
                    <Logo className="h-24 w-auto" />
                </Link>
                <div>
                    <div className="overline mb-3">// forge account</div>
                    <div className="font-display text-4xl tracking-tight leading-tight">
                        Ninety seconds<br />to first insight.
                    </div>
                </div>
                <div className="font-mono text-xs text-muted-foreground space-y-1">
                    <div>· Full SEO + AEO suite</div>
                    <div>· No credit card</div>
                    <div>· Unlimited projects</div>
                </div>
            </div>

            <div className="flex items-center justify-center px-6 py-12">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
                    <div className="overline mb-3">Create account</div>
                    <h1 className="font-display text-3xl tracking-tight mb-4">Start ranking.</h1>
                    {refCode && (
                        <div className="mb-6 border border-primary/60 bg-primary/5 p-3" data-testid="referral-banner">
                            <div className="text-xs text-primary font-mono">Referred via {refCode}</div>
                            <div className="text-xs text-muted-foreground mt-1">Your referrer will earn a free Pro month once you upgrade — thanks!</div>
                        </div>
                    )}
                    {inviteInfo && (
                        <div className="mb-6 border border-primary/60 bg-primary/5 p-3" data-testid="invite-banner">
                            <div className="text-xs text-primary font-mono">TEAM INVITE</div>
                            <div className="text-xs mt-1">
                                <span className="text-foreground">{inviteInfo.owner_name || inviteInfo.owner_email}</span>
                                <span className="text-muted-foreground"> added you as a <span className="text-foreground">{inviteInfo.role}</span>. You'll share their plan + credits — no billing.</span>
                            </div>
                        </div>
                    )}

                    <label className="text-xs text-muted-foreground block mb-2">Name</label>
                    <input
                        data-testid="register-name-input"
                        required value={name} onChange={(e) => setName(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="Ada Lovelace"
                    />
                    <label className="text-xs text-muted-foreground block mt-5 mb-2">Email</label>
                    <input
                        data-testid="register-email-input"
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="you@company.com"
                    />
                    <label className="text-xs text-muted-foreground block mt-5 mb-2">Password</label>
                    <input
                        data-testid="register-password-input"
                        type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                        placeholder="min 6 chars"
                    />
                    <button
                        data-testid="register-submit-btn"
                        disabled={loading}
                        className="w-full mt-8 bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                        {loading ? "Creating…" : "Create account →"}
                    </button>
                    <GoogleSignInButton label="Sign up with Google" />
                    <div className="mt-6 text-xs text-muted-foreground">
                        Already have an account? <Link to="/login" data-testid="switch-login-link" className="text-primary hover:underline">Sign in</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
